/**
 * BusinessAlertModal — 08/02/2026
 *
 * Renders the next pending business alert from `state.businessAlerts` as a
 * forced popup that the player must explicitly acknowledge. Triggers come
 * from various game systems (depot overload, bad season, concurrent steals
 * a client, etc.) via `pushBusinessAlert(alert)`.
 *
 * Severity drives the visual treatment :
 *   • 'info'   → sky blue
 *   • 'warn'   → amber
 *   • 'danger' → rose
 *
 * Each alert is `{ id, severity, title, body, kind, raisedOn, cta }`.
 * `cta` optional : `{ label, onClick }` for an inline call-to-action.
 */
import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, AlertCircle, Info, X } from 'lucide-react';
import useGameStore from '../../store/gameStore';
import { EMPTY_ARRAY } from '../../store/stableEmpties';
import MarkdownLite from '../MarkdownLite';

const SEVERITY_DEF = {
  info:   { Icon: Info,           color: 'sky',
            ring: 'border-sky-400/60 shadow-[0_0_50px_-10px_rgba(56,189,248,0.5)]',
            bg: 'bg-gradient-to-br from-sky-900/40 via-ink-900 to-sky-950/30',
            titleColor: 'text-sky-200' },
  warn:   { Icon: AlertCircle,    color: 'amber',
            ring: 'border-amber-400/60 shadow-[0_0_50px_-10px_rgba(251,191,36,0.5)]',
            bg: 'bg-gradient-to-br from-amber-900/40 via-ink-900 to-amber-950/30',
            titleColor: 'text-amber-200' },
  danger: { Icon: AlertTriangle,  color: 'rose',
            ring: 'border-rose-400/60 shadow-[0_0_50px_-10px_rgba(244,63,94,0.5)]',
            bg: 'bg-gradient-to-br from-rose-900/40 via-ink-900 to-rose-950/30',
            titleColor: 'text-rose-200' },
};

export default function BusinessAlertModal() {
  const alerts = useGameStore(s => s.businessAlerts || EMPTY_ARRAY);
  const dismiss = useGameStore(s => s.dismissBusinessAlert);
  const next = alerts[0] || null;
  if (!next) return null;
  const def = SEVERITY_DEF[next.severity] || SEVERITY_DEF.info;
  const Icon = def.Icon;
  return (
    <AnimatePresence>
      <motion.div
        key={`business-alert-${next.id}`}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
        data-testid="business-alert-overlay"
        onClick={() => dismiss(next.id)}
      >
        <motion.div
          initial={{ scale: 0.85, y: 30 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 240, damping: 22 }}
          className={`relative w-full max-w-md rounded-2xl border-2 p-5 shadow-2xl ${def.ring} ${def.bg}`}
          onClick={(e) => e.stopPropagation()}
          data-testid={`business-alert-${next.kind}`}
        >
          <button
            type="button"
            onClick={() => dismiss(next.id)}
            className="absolute top-2 right-2 p-1.5 rounded hover:bg-white/10 text-white/60 hover:text-white"
            aria-label="Fermer"
            data-testid="business-alert-close"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-2.5 mb-3">
            <div className={`p-2 rounded-lg bg-${def.color}-500/20 border border-${def.color}-400/30`}>
              <Icon className={`w-5 h-5 ${def.titleColor}`} />
            </div>
            <h2 className={`text-base font-bold ${def.titleColor} flex-1`}>{next.title}</h2>
          </div>
          <div className="text-sm text-slate-300 leading-relaxed space-y-2 mb-4">
            <MarkdownLite text={next.body} />
          </div>
          <div className="flex items-center justify-end gap-2">
            {next.cta && (
              <button
                type="button"
                onClick={() => { try { next.cta.onClick?.(); } catch { /* ignore */ } dismiss(next.id); }}
                className={`px-3 py-1.5 rounded-md text-sm font-bold border bg-${def.color}-500/20 border-${def.color}-400/50 text-${def.color}-100 hover:bg-${def.color}-500/30`}
                data-testid="business-alert-cta"
              >
                {next.cta.label}
              </button>
            )}
            <button
              type="button"
              onClick={() => dismiss(next.id)}
              className="px-3 py-1.5 rounded-md text-sm font-bold border border-slate-500 bg-slate-700/40 hover:bg-slate-700/70 text-slate-100"
              data-testid="business-alert-dismiss"
            >
              OK, j&apos;ai compris
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
